import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { usePerformanceRuns, useApplySuggestion } from '../Performance/usePerformance';
import { buildWorkspaceSuggestionEdit } from './workspaceSuggestionEdit';

// The assistant never carries an executable payload. It cites a stored analysis
// suggestion by (messageId, index); the server keeps only citations that name a
// suggestion the page listed from a completed run of this campaign. This module
// resolves a citation to the SAME card the recommendation panel renders, from the
// same /performance/runs read, and applies it through the card's own
// useApplySuggestion + buildWorkspaceSuggestionEdit path — tagged
// (optimizeMessageId, suggestionIndex), so the card reads as applied after a reload.

const key = (messageId, index) => `${messageId}:${index}`;
const value = v => (v == null ? null : typeof v === 'object' ? JSON.stringify(v) : String(v));

export const AssistantCitationContext = createContext(null);

/** Compact descriptive list sent with a question; the model cites these by id. */
export function citableRecommendations(cards = []) {
  return cards.filter(card => card.messageId && Number.isInteger(card.index) && card.route !== 'hidden')
    .slice(0, 80).map(card => ({
      messageId: String(card.messageId), index: card.index, analysisKind: kindOf(card),
      headline: card.head || card.plain || null, action: card.action, targetType: card.tt, targetName: card.tn, targetId: card.tid,
      currentValue: value(card.cur), recommendedValue: value(card.rec), applicable: card.auto === true,
      advisoryReason: card.auto ? null : card.advisoryReason || null,
      alreadyApplied: Boolean(card.appliedEdit && card.appliedEdit.status !== 'failed'),
    }));
}
const kindOf = card => ({ diagnostics: 'diagnostic', pacing: 'pacing', recommendations: 'performance' }[card.route] || null);

/** The stored card a citation names, including one folded into a consolidated card. */
export function findCitedCard(cards = [], messageId, index) {
  return cards.find(card => key(card.messageId, card.index) === key(messageId, index))
    || cards.find(card => (card.alsoReportedBy || []).some(source => key(source.messageId, source.index) === key(messageId, index)))
    || null;
}

export function useAssistantSuggestions({ accountId, userId, campaignId, appliedVersion = 0, onApplied }) {
  const scope = useMemo(() => (campaignId ? [campaignId] : []), [campaignId]);
  const runs = usePerformanceRuns(campaignId ? accountId : null, userId, campaignId || null, scope, true);
  const { reload } = runs;
  const { apply, applying, notice, clearNotice } = useApplySuggestion({
    accountId, campaignId, destination: runs.campaign?.destination || null, userId,
    buildEditRequest: buildWorkspaceSuggestionEdit,
  });
  const [applied, setApplied] = useState({});
  // An Apply on the recommendation panel bumps the version: re-read the runs so
  // this view shows the same applied state.
  useEffect(() => { if (appliedVersion) reload(); }, [appliedVersion, reload]);
  const applyCard = useCallback(async (card) => {
    const out = await apply(card);
    if (out?.ok) {
      setApplied(current => ({ ...current, [card.id]: true }));
      reload(); onApplied?.();
    }
    return out;
  }, [apply, reload, onApplied]);
  return { cards: campaignId ? runs.cards : [], loading: runs.loading, error: runs.error, apply: applyCard, applying, applied, notice, clearNotice };
}

/** One cited suggestion: the card's Apply control, its applied state, or its advisory reason. */
export function CitedSuggestion({ suggestion }) {
  const context = useContext(AssistantCitationContext);
  if (!context) return null;
  const { campaignId, citations, suggestions } = context;
  const cite = citations.find(row => key(row.messageId, row.index) === key(suggestion.messageId, suggestion.index));
  if (!cite) return null;
  if (String(cite.campaignId) !== String(campaignId)) return <p className="ow-assistant-cite">Open this answer’s campaign to apply its linked recommendation.</p>;
  if (suggestions.loading && !suggestions.cards.length) return <p className="ow-assistant-cite" role="status">Loading the linked recommendation…</p>;
  const card = findCitedCard(suggestions.cards, cite.messageId, cite.index);
  if (!card) return <p className="ow-assistant-cite">The linked recommendation is no longer in this campaign’s latest analysis.</p>;
  const label = card.head || card.plain || 'Linked recommendation';
  const edit = card.appliedEdit && card.appliedEdit.status !== 'failed' ? card.appliedEdit : null;
  if (suggestions.applied[card.id] || edit) {
    return <p className="ow-assistant-cite ow-assistant-cite-applied" role="status">
      {edit?.status === 'applying' ? 'Applying' : 'Applied'} · {label}</p>;
  }
  if (!card.auto) {
    return <p className="ow-assistant-cite"><span className="ow-assistant-advisory">Advisory</span> {card.advisoryReason || 'This change has to be made in the platform.'}</p>;
  }
  const busy = Boolean(suggestions.applying[card.id]);
  return <p className="ow-assistant-cite">
    <button type="button" className="ow-button ow-button-primary rc-apply" disabled={busy}
      aria-label={`Apply: ${label}`} onClick={() => suggestions.apply(card)}>{busy ? 'Applying…' : 'Apply'}</button>
    {' '}<span>{label}</span>
    {card.appliedEdit?.status === 'failed' && <span className="ow-assistant-cite-failed"> · Last apply failed{card.appliedEdit.error ? `: ${card.appliedEdit.error}` : ''}</span>}
  </p>;
}
